import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import { useAuthQuery } from '../../../../auth/hooks/useAuthQuery';

import {
  DEFAULT_CENTER_COORDS,
  getPlaceLatLngByAddress,
  INITIAL_ZOOM_LEVEL,
  SET_PIN_ZOOM_LEVEL,
} from './LocationMap.helpers';

interface IUseMapDetails {
  locationCoordinates?: google.maps.LatLngLiteral;
  isDesktop?: boolean;
  mapInstance?: google.maps.Map;
}

export const useMapCenter = ({ locationCoordinates, mapInstance, isDesktop = true }: IUseMapDetails) => {
  const [center, setCenter] = useState<{ lat: number; lng: number }>(DEFAULT_CENTER_COORDS);
  const { userSettings } = useAuthQuery();
  const [hasSetAccountCenter, setHasSetAccountCenter] = useState(false);

  const getAccountSettingsLocation = useCallback(async () => {
    if (userSettings && mapInstance && !hasSetAccountCenter) {
      const response = await getPlaceLatLngByAddress(userSettings.billingAddress?.replace(', USA', ''), mapInstance);

      if (response) {
        setCenter(response);
        setHasSetAccountCenter(true);
      }
    }
  }, [userSettings, mapInstance, hasSetAccountCenter]);

  const fitMapBounds = useCallback(() => {
    if (mapInstance) {
      if (locationCoordinates) {
        const bounds = new window.google.maps.LatLngBounds();

        bounds.extend(locationCoordinates);
        // paddings were added in order to fit any kind of route length and orientation
        mapInstance.fitBounds(bounds, {
          top: 100,
          right: 100,
          bottom: isDesktop ? 300 : 100,
          left: 100,
        });
        mapInstance.panBy(0, 50);
        mapInstance.setZoom(SET_PIN_ZOOM_LEVEL);
      } else {
        mapInstance.setCenter(center);
        mapInstance.setZoom(INITIAL_ZOOM_LEVEL);
      }
    }
  }, [center, mapInstance, locationCoordinates, isDesktop]);

  const debouncedFitMapBounds = useMemo(() => debounce(fitMapBounds, 300), [fitMapBounds]);

  useEffect(() => {
    center.lat === DEFAULT_CENTER_COORDS.lat &&
      center.lng === DEFAULT_CENTER_COORDS.lng &&
      getAccountSettingsLocation();
  }, [getAccountSettingsLocation, locationCoordinates]);

  useEffect(() => {
    if (locationCoordinates && mapInstance) {
      // For location changes, use debounced version
      debouncedFitMapBounds();
    }
  }, [mapInstance, locationCoordinates, debouncedFitMapBounds]);

  useEffect(() => {
    // Only set center and zoom when there's no location and map is ready
    if (!locationCoordinates && mapInstance && center) {
      mapInstance.setCenter(center);
      mapInstance.setZoom(INITIAL_ZOOM_LEVEL);
    }
  }, [center, mapInstance, locationCoordinates]);

  return { center };
};
